import { inject, Injectable, Injector } from '@angular/core'

import { BehaviorSubject, combineLatest, debounceTime, switchMap, takeUntil, tap } from 'rxjs'

import { UserProfile } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { Memoize } from '@ti/core/common/decorators'
import { exposeReadonlyState, injectDestroy$, toSignalState } from '@ti/core/common/utils'

@Injectable()
export class FleetOwnerProvider {
  protected readonly api = inject(ApiService)
  protected readonly injector = inject(Injector)
  protected readonly destroy$ = injectDestroy$()

  protected readonly store$ = {
    fleetId: new BehaviorSubject<number | null>(null),
    owner: new BehaviorSubject<UserProfile | undefined>(undefined),
    isLoading: new BehaviorSubject<boolean>(false),
    refresh: new BehaviorSubject<void>(undefined),
  }

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((e) => console.error('Cannot init FleetOwnerModel', e))
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      ...exposeReadonlyState(this.store$, ['owner', 'fleetId', 'isLoading']),
    }
  }

  public set fleetId(value: number) {
    if (value !== this.store$.fleetId.getValue()) {
      this.store$.fleetId.next(value)
    }
  }

  public refresh() {
    this.store$.refresh.next()
  }

  protected async init() {
    combineLatest([this.store$.fleetId, this.store$.refresh])
      .pipe(
        debounceTime(128),
        tap(() => this.store$.isLoading.next(true)),
        switchMap(([fleetId]) =>
          fleetId ? this.api.tsp.fleetUser.getOwner$(fleetId) : Promise.resolve(undefined),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((ownerData) => {
        this.store$.owner.next(ownerData)
        this.store$.isLoading.next(false)
      })
  }
}
